import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Stage, Layer, Rect, Line, Group, Circle } from 'react-konva';
import {
  Box,
  Text
} from '@chakra-ui/react';
import ObjectPropertiesSidebar from './ObjectPropertiesSidebar';

// Measurement constants for standardization
const MEASUREMENT_CONSTANTS = {
  PIXELS_PER_INCH: 1, // 1 pixel = 1 inch for simplicity
  INCHES_PER_FOOT: 12,
  STANDARD_GRID_FEET: 5, // 5 foot grid
  STANDARD_ROOM_FEET: 30, // 30x30 ft standard room
  
  // Calculated values
  get PIXELS_PER_FOOT() { return this.PIXELS_PER_INCH * this.INCHES_PER_FOOT; }, // 12 pixels = 1 foot
  get GRID_PIXELS() { return this.STANDARD_GRID_FEET * this.PIXELS_PER_FOOT; }, // 60 pixels = 5 feet
  get STANDARD_ROOM_PIXELS() { return this.STANDARD_ROOM_FEET * this.PIXELS_PER_FOOT; }, // 360 pixels = 30 feet
};

const FloorPlanCanvas = forwardRef(({ projectId, d2ViewId, onSave, initialData = null, sidebarWidth = 300, isSidebarCollapsed = false }, ref) => {
  const stageRef = useRef(null);
  const [stage, setStage] = useState(null);
  const [selectedTool, setSelectedTool] = useState('select');
  const [selectedObject, setSelectedObject] = useState(null);
  const [roomDimensions, setRoomDimensions] = useState({ 
    width: MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS, 
    height: MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS 
  }); // 30ft x 30ft standard room
  const [wallLength, setWallLength] = useState(MEASUREMENT_CONSTANTS.STANDARD_ROOM_PIXELS); // 30ft wall
  const [windowLength, setWindowLength] = useState(MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 5); // 5ft window
  const [doorLength, setDoorLength] = useState(MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT * 3); // 3ft door
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [tempLine, setTempLine] = useState(null);
  const [draftObjects, setDraftObjects] = useState([]); // Store draft objects
  const [canvasObjects, setCanvasObjects] = useState([]); // Store canvas objects
  const [nextId, setNextId] = useState(1); // For unique object IDs
  const [isPropertiesSidebarOpen, setIsPropertiesSidebarOpen] = useState(false); // Properties sidebar state
  const [snapPoints, setSnapPoints] = useState([]); // Store snap points for room placement
  const [windowDoorSnapPoints, setWindowDoorSnapPoints] = useState([]); // Store snap points for windows/doors
  const [productGridSnapPoints, setProductGridSnapPoints] = useState([]); // Store grid snap points for products
  const [isPlacingRoom, setIsPlacingRoom] = useState(false); // Track if we're in room placement mode
  const [isPlacingWindow, setIsPlacingWindow] = useState(false); // Track if we're in window placement mode
  const [isPlacingDoor, setIsPlacingDoor] = useState(false); // Track if we're in door placement mode
  const [isPlacingProduct, setIsPlacingProduct] = useState(false); // Track if we're in product placement mode
  const [placementPreview, setPlacementPreview] = useState(null); // Preview for room placement
  const [currentProduct, setCurrentProduct] = useState(null); // Track current product being placed
  const [doorAnchorPoints, setDoorAnchorPoints] = useState([]); // Track door anchor points for 2-point placement
  const [isPanning, setIsPanning] = useState(false); // Track if canvas is being panned
  const [panStart, setPanStart] = useState({ x: 0, y: 0 }); // Track pan start position
  const [draggedObject, setDraggedObject] = useState(null); // Store the object being dragged for restoration

  // Calculate canvas dimensions based on sidebar state
  const getCanvasDimensions = useCallback(() => {
    const sidebarW = isSidebarCollapsed ? 50 : sidebarWidth;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const headerHeight = 120; // Header + subheader height
    
    return {
      width: windowWidth - sidebarW - 40, // 40px for padding
      height: windowHeight - headerHeight - 40 // 40px for padding
    };
  }, [isSidebarCollapsed, sidebarWidth]);

  // Calculate snap points from existing rooms
  const calculateSnapPoints = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    const points = [];

    rooms.forEach(room => {
      const { x, y, width, height } = room;
      
      // Top edge snap points
      for (let i = 0; i <= width; i += MEASUREMENT_CONSTANTS.GRID_PIXELS) { // 5ft grid (60px)
        points.push({ x: x + i, y: y, type: 'top' });
      }
      
      // Bottom edge snap points
      for (let i = 0; i <= width; i += MEASUREMENT_CONSTANTS.GRID_PIXELS) {
        points.push({ x: x + i, y: y + height, type: 'bottom' });
      }
      
      // Left edge snap points
      for (let i = 0; i <= height; i += MEASUREMENT_CONSTANTS.GRID_PIXELS) {
        points.push({ x: x, y: y + i, type: 'left' });
      }
      
      // Right edge snap points
      for (let i = 0; i <= height; i += MEASUREMENT_CONSTANTS.GRID_PIXELS) {
        points.push({ x: x + width, y: y + i, type: 'right' });
      }
    });

    setSnapPoints(points);
    return points;
  }, [canvasObjects]);

  // Calculate snap points for windows and doors on room walls
  const calculateWindowDoorSnapPoints = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    const points = [];

    rooms.forEach(room => {
      const { x, y, width, height } = room;
      
      const margin = MEASUREMENT_CONSTANTS.PIXELS_PER_FOOT; // 1ft margin from corners
      
      // Helper function to calculate equidistant snap points
      const calculateEquidistantPoints = (wallLength) => {
        const availableLength = wallLength - (2 * margin); // Subtract margins from both ends
        const maxPoints = Math.floor(availableLength / MEASUREMENT_CONSTANTS.GRID_PIXELS); // Max points that can fit
        
        if (maxPoints <= 0) return []; // Wall too small for any snap points
        
        // Calculate actual spacing to make points equidistant
        const actualSpacing = availableLength / (maxPoints + 1);
        const snapPositions = [];
        
        for (let i = 1; i <= maxPoints; i++) {
          snapPositions.push(margin + (actualSpacing * i));
        }
        
        return snapPositions;
      };
      
      // Top wall snap points (equidistant)
      const topBottomSnapPositions = calculateEquidistantPoints(width);
      topBottomSnapPositions.forEach(position => {
        points.push({ 
          x: x + position, 
          y: y, 
          type: 'top',
          roomId: room.id,
          wallType: 'horizontal'
        });
      });
      
      // Bottom wall snap points (equidistant)
      topBottomSnapPositions.forEach(position => {
        points.push({ 
          x: x + position, 
          y: y + height, 
          type: 'bottom',
          roomId: room.id,
          wallType: 'horizontal'
        });
      });
      
      // Left and right wall snap points (equidistant)
      const leftRightSnapPositions = calculateEquidistantPoints(height);
      leftRightSnapPositions.forEach(position => {
        points.push({ 
          x: x, 
          y: y + position, 
          type: 'left',
          roomId: room.id,
          wallType: 'vertical'
        });
      });
      
      leftRightSnapPositions.forEach(position => {
        points.push({ 
          x: x + width, 
          y: y + position, 
          type: 'right',
          roomId: room.id,
          wallType: 'vertical'
        });
      });
    });

    setWindowDoorSnapPoints(points);
    return points;
  }, [canvasObjects]);

  // Calculate grid snap points for products inside rooms
  const calculateProductGridSnapPoints = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    const points = [];
    const gridSize = MEASUREMENT_CONSTANTS.GRID_PIXELS; // 5ft grid (60px) for product placement

    rooms.forEach(room => {
      const { x, y, width, height } = room;
      const margin = 0; // No margin to allow snap points right at the walls
      
      // Helper function to calculate equidistant grid positions
      const calculateEquidistantGridPositions = (roomDimension) => {
        const availableLength = roomDimension - (2 * margin); // Subtract margins from both ends
        const maxPoints = Math.floor(availableLength / gridSize); // Max points that can fit
        
        if (maxPoints <= 0) return []; // Room too small for any grid points
        
        // Calculate actual spacing to make points equidistant
        const actualSpacing = availableLength / (maxPoints);
        const gridPositions = [];
        
        for (let i = 0; i <= maxPoints; i++) {
          gridPositions.push(margin + (actualSpacing * i));
        }
        
        return gridPositions;
      };
      
      // Calculate equidistant positions for both X (west-east) and Y (north-south) directions
      const xPositions = calculateEquidistantGridPositions(width);
      const yPositions = calculateEquidistantGridPositions(height);
      
      // Generate grid points using equidistant spacing
      xPositions.forEach(relativeX => {
        yPositions.forEach(relativeY => {
          points.push({
            x: x + relativeX,
            y: y + relativeY,
            roomId: room.id,
            type: 'grid'
          });
        });
      });
    });

    setProductGridSnapPoints(points);
    return points;
  }, [canvasObjects]);

  // Find nearest snap point
  const findNearestSnapPoint = useCallback((mouseX, mouseY, snapDistance = 50) => {
    let nearestPoint = null;
    let minDistance = Infinity;

    snapPoints.forEach(point => {
      const distance = Math.sqrt(Math.pow(mouseX - point.x, 2) + Math.pow(mouseY - point.y, 2));
      if (distance < minDistance && distance < snapDistance) {
        minDistance = distance;
        nearestPoint = point;
      }
    });

    return nearestPoint;
  }, [snapPoints]);

  // Validate room placement (must be adjacent to existing room)
  const validateRoomPlacement = useCallback((x, y, width, height) => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    
    if (rooms.length === 0) {
      // First room can be placed anywhere
      return true;
    }

    // Check if the new room touches any existing room
    const newRoom = { x, y, width, height };
    
    return rooms.some(existingRoom => {
      // Check if rooms are adjacent (share a wall)
      const touches = 
        // New room's right edge touches existing room's left edge
        (newRoom.x + newRoom.width === existingRoom.x) ||
        // New room's left edge touches existing room's right edge
        (newRoom.x === existingRoom.x + existingRoom.width) ||
        // New room's bottom edge touches existing room's top edge
        (newRoom.y + newRoom.height === existingRoom.y) ||
        // New room's top edge touches existing room's bottom edge
        (newRoom.y === existingRoom.y + existingRoom.height);
      
      // Also check for overlap
      const overlaps = !(
        newRoom.x + newRoom.width <= existingRoom.x ||
        newRoom.x >= existingRoom.x + existingRoom.width ||
        newRoom.y + newRoom.height <= existingRoom.y ||
        newRoom.y >= existingRoom.y + existingRoom.height
      );
      
      return touches && !overlaps;
    });
  }, [canvasObjects]);

  // Initialize Konva stage
  useEffect(() => {
    if (stageRef.current && !stage) {
      const dimensions = getCanvasDimensions();
      
      const konvaStage = stageRef.current;
      setStage(konvaStage);
      
      console.log('Konva stage initialized successfully');
      // Note: No automatic room creation - first room is only added when user clicks "Add Room"
    }
  }, [stageRef, getCanvasDimensions]);

      // Load initial data if provided
  useEffect(() => {
    if (initialData && canvasObjects.length === 0) {
      console.log('Loading initial data:', initialData);
      try {
        const objects = initialData.objects || [];
        const loadedObjects = objects.map((obj, index) => ({
          id: `obj_${nextId + index}`,
          ...obj,
          draggable: obj.type === 'room' ? false : true, // Rooms from API are not draggable
          selectable: true
        }));
        
        setCanvasObjects(loadedObjects);
        setNextId(prev => prev + objects.length);
        console.log('Initial data loaded, objects count:', loadedObjects.length);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    }
  }, [initialData, canvasObjects.length, nextId]);

  // Handle canvas resize when sidebar toggles
  useEffect(() => {
    if (stage) {
      const dimensions = getCanvasDimensions();
      stage.width(dimensions.width);
      stage.height(dimensions.height);
      stage.draw();
    }
  }, [isSidebarCollapsed, sidebarWidth, stage, getCanvasDimensions]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (stage) {
        const dimensions = getCanvasDimensions();
        stage.width(dimensions.width);
        stage.height(dimensions.height);
        stage.draw();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [stage, getCanvasDimensions]);

  const addRoom = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    
    if (rooms.length === 0) {
      // First room goes in center when user explicitly clicks "Add Room"
      const dimensions = getCanvasDimensions();
      const centerX = dimensions.width / 2;
      const centerY = dimensions.height / 2;
      
      addRoomAtPosition(centerX, centerY);
    } else {
      // Subsequent rooms use snap system
      setIsPlacingRoom(true);
      calculateSnapPoints();
    }
  }, [canvasObjects, getCanvasDimensions, calculateSnapPoints]);

  const addRoomAtPosition = useCallback((x, y) => {
    const room = {
      id: `room_${nextId}`,
      type: 'room',
      x: x - roomDimensions.width / 2,
      y: y - roomDimensions.height / 2,
      width: roomDimensions.width,
      height: roomDimensions.height,
      fill: 'rgba(195, 193, 193, 0.72)',
      stroke: '#333',
      strokeWidth: 4,
      draggable: false, // Rooms are not draggable
      selectable: true,
      data: { 
        type: 'room',
        width: roomDimensions.width,
        height: roomDimensions.height
      }
    };
    
    // Add to canvas objects (not draft objects for rooms)
    setCanvasObjects(prev => [...prev, room]);
    setNextId(prev => prev + 1);
    
    // Recalculate snap points after adding room
    setTimeout(() => calculateSnapPoints(), 100);
  }, [roomDimensions, nextId, calculateSnapPoints]);

  const addWall = useCallback((startPoint, endPoint) => {
    const wall = {
      id: `wall_${nextId}`,
      type: 'wall',
      points: [startPoint.x, startPoint.y, endPoint.x, endPoint.y],
      stroke: '#333',
      strokeWidth: 4,
      draggable: true,
      selectable: true,
      data: { type: 'wall' }
    };
    
    setCanvasObjects(prev => [...prev, wall]);
    setNextId(prev => prev + 1);
  }, [nextId]);

  const addWindow = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    
    if (rooms.length === 0) {
      console.log('No rooms available for window placement');
      return;
    }
    
    // Enter window placement mode
    setIsPlacingWindow(true);
    calculateWindowDoorSnapPoints();
  }, [canvasObjects, calculateWindowDoorSnapPoints]);

  const addWindowAtPosition = useCallback((x, y, wallType = 'horizontal') => {
    const length = windowLength;
    let windowX, windowY, windowWidth, windowHeight;
    
    if (wallType === 'horizontal') {
      // For horizontal walls, window should be horizontal (parallel to wall)
      windowX = x - length / 2; // Center the length
      windowY = y - 4; // Center the 8px width
      windowWidth = length;
      windowHeight = 8;
    } else {
      // For vertical walls, window should be vertical (parallel to wall)
      windowX = x - 4; // Center the 8px width
      windowY = y - length / 2; // Center the length
      windowWidth = 8;
      windowHeight = length;
    }
    
    const window = {
      id: `window_${nextId}`,
      type: 'window',
      x: windowX,
      y: windowY,
      width: windowWidth,
      height: windowHeight,
      fill: 'rgba(135, 206, 250, 0.6)', // Light cyan
      stroke: '#333',
      strokeWidth: 1,
      draggable: true,
      selectable: true,
      wallType: wallType, // Track which wall it's on
      data: { 
        type: 'window',
        width: windowWidth,
        height: windowHeight
      }
    };
    
    // Add to canvas objects
    setCanvasObjects(prev => [...prev, window]);
    setNextId(prev => prev + 1);
  }, [windowLength, nextId]);

  const addDoor = useCallback(() => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    
    if (rooms.length === 0) {
      console.log('No rooms available for door placement');
      return;
    }
    
    // Enter door placement mode
    setIsPlacingDoor(true);
    calculateWindowDoorSnapPoints();
  }, [canvasObjects, calculateWindowDoorSnapPoints]);

  const addDoorAtPosition = useCallback((x, y, wallType = 'horizontal', customLength = null) => {
    const length = customLength || doorLength;
    let doorX, doorY, doorWidth, doorHeight;
    
    if (wallType === 'horizontal') {
      // For horizontal walls, door should be horizontal (parallel to wall)
      doorX = x - length / 2; // Center the length
      doorY = y - 4; // Center the 8px width
      doorWidth = length;
      doorHeight = 8;
    } else {
      // For vertical walls, door should be vertical (parallel to wall)
      doorX = x - 4; // Center the 8px width
      doorY = y - length / 2; // Center the length
      doorWidth = 8;
      doorHeight = length;
    }
    
    const door = {
      id: `door_${nextId}`,
      type: 'door',
      x: doorX,
      y: doorY,
      width: doorWidth,
      height: doorHeight,
      fill: 'rgba(205, 133, 63, 0.8)', // Light brown
      stroke: '#333',
      strokeWidth: 1,
      draggable: true,
      selectable: true,
      wallType: wallType, // Track which wall it's on
      data: { 
        type: 'door',
        width: doorWidth,
        height: doorHeight
      }
    };
    
    // Add to canvas objects
    setCanvasObjects(prev => [...prev, door]);
    setNextId(prev => prev + 1);
  }, [doorLength, nextId]);

  const addProduct = useCallback((product) => {
    const rooms = canvasObjects.filter(obj => obj.type === 'room');
    
    if (rooms.length === 0) {
      console.log('No rooms available for product placement');
      return;
    }
    
    // Store the product data and enter placement mode
    setCurrentProduct(product);
    setIsPlacingProduct(true);
    
    // Use wall snap points for rectangular products (1, 2, 3), grid points for others
    if (product.id === 1 || product.id === 2 || product.id === 3) {
      calculateWindowDoorSnapPoints(); // Use wall snap points for rectangular products
    } else {
      calculateProductGridSnapPoints(); // Use grid snap points for circular products
    }
  }, [canvasObjects, calculateProductGridSnapPoints, calculateWindowDoorSnapPoints]);

  const addProductAtPosition = useCallback((x, y, productData, wallType = 'horizontal') => {
    // Determine product dimensions and shape based on product ID
    let productObject;
    const productId = productData.id;
    
    if (productId === 1 || productId === 2) {
      // Products 1 and 2: Same length as windows, rotate based on wall type
      const length = windowLength; // Use window length
      let productX, productY, productWidth, productHeight;
      
      if (wallType === 'horizontal') {
        // For horizontal walls, product should be horizontal (parallel to wall)
        productX = x - length / 2; // Center the length
        productY = y - 4; // Center the 8px width
        productWidth = length;
        productHeight = 8;
      } else {
        // For vertical walls, product should be vertical (parallel to wall)
        productX = x - 4; // Center the 8px width
        productY = y - length / 2; // Center the length
        productWidth = 8;
        productHeight = length;
      }
      
      productObject = {
        id: `product_${nextId}`,
        type: 'product',
        x: productX,
        y: productY,
        width: productWidth,
        height: productHeight,
        fill: 'rgba(255, 255, 255, 0.9)', // White with transparency
        stroke: '#333',
        strokeWidth: 2,
        draggable: true,
        selectable: true,
        shape: 'rectangle',
        wallType: wallType, // Track which wall it's on
        data: { 
          type: 'product',
          productId: productData.id,
          productName: productData.name,
          productData: productData,
          width: productWidth,
          height: productHeight
        }
      };
    } else if (productId === 3) {
      // Product 3: Same width as product 1, double the length
      const length = windowLength * 2; // Double the window length
      let productX, productY, productWidth, productHeight;
      
      if (wallType === 'horizontal') {
        // For horizontal walls, product should be horizontal (parallel to wall)
        productX = x - length / 2; // Center the length
        productY = y - 4; // Center the 8px width
        productWidth = length;
        productHeight = 8;
      } else {
        // For vertical walls, product should be vertical (parallel to wall)
        productX = x - 4; // Center the 8px width
        productY = y - length / 2; // Center the length
        productWidth = 8;
        productHeight = length;
      }
      
      productObject = {
        id: `product_${nextId}`,
        type: 'product',
        x: productX,
        y: productY,
        width: productWidth,
        height: productHeight,
        fill: 'rgba(255, 255, 255, 0.9)', // White with transparency
        stroke: '#333',
        strokeWidth: 2,
        draggable: true,
        selectable: true,
        shape: 'rectangle',
        wallType: wallType, // Track which wall it's on
        data: { 
          type: 'product',
          productId: productData.id,
          productName: productData.name,
          productData: productData,
          width: productWidth,
          height: productHeight
        }
      };
    } else {
      // Default: Circle for other product IDs
      const radius = 10;
      productObject = {
        id: `product_${nextId}`,
        type: 'product',
        x: x - radius, // Center the circle
        y: y - radius, // Center the circle
        radius: radius, // Circle radius
        fill: 'rgba(255, 255, 255, 0.9)', // White with transparency
        stroke: '#333',
        strokeWidth: 2,
        draggable: true,
        selectable: true,
        shape: 'circle',
        data: { 
          type: 'product',
          productId: productData.id,
          productName: productData.name,
          productData: productData
        }
      };
    }
    
    // Add to canvas objects
    setCanvasObjects(prev => [...prev, productObject]);
    setNextId(prev => prev + 1);
  }, [nextId, windowLength]);

  const deleteSelected = useCallback(() => {
    if (!selectedObject) return;

    setCanvasObjects(prev => prev.filter(obj => obj.id !== selectedObject.id));
    setDraftObjects(prev => prev.filter(obj => obj.id !== selectedObject.id));
    setSelectedObject(null);
    setIsPropertiesSidebarOpen(false);
    
    // Note: Since rooms are not draggable, we don't need to recalculate snap points here
    // Snap points will be recalculated automatically via the useEffect when canvasObjects changes
  }, [selectedObject]);

  const clearCanvas = useCallback(() => {
    setCanvasObjects([]);
    setDraftObjects([]);
    setSelectedObject(null);
    setIsPropertiesSidebarOpen(false);
    setSnapPoints([]); // Clear snap points when canvas is cleared
  }, []);

  const saveCanvas = useCallback(() => {
    console.log('=== Save Canvas Debug ===');
    console.log('Draft objects count:', draftObjects.length);
    console.log('Canvas objects count before adding drafts:', canvasObjects.length);

    // Add all draft objects to canvas
    let finalObjects = [...canvasObjects];
    if (draftObjects.length > 0) {
      finalObjects = [...finalObjects, ...draftObjects];
      setDraftObjects([]);
      console.log('Canvas objects count after adding drafts:', finalObjects.length);
    }
    
    // Log the data being saved for debugging
    console.log('Saving canvas data:', { objects: finalObjects });
    console.log('Objects in canvas:', finalObjects.length);
    finalObjects.forEach((obj, index) => {
      console.log(`Object ${index}:`, {
        id: obj.id,
        name: obj.type,
        type: obj.type,
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        data: obj.data
      });
    });
    
    onSave({ objects: finalObjects });
  }, [canvasObjects, draftObjects, onSave]);

  const loadCanvas = useCallback((data) => {
    if (!data || !data.objects) return;

    const objects = data.objects.map((obj, index) => ({
      id: `obj_${nextId + index}`,
      ...obj,
      draggable: obj.type === 'room' ? false : true, // Rooms are not draggable
      selectable: true
    }));
    
    setCanvasObjects(objects);
    setNextId(prev => prev + objects.length);
  }, [nextId]);

  const undo = useCallback(() => {
    // TODO: Implement undo functionality
  }, []);

  const redo = useCallback(() => {
    // TODO: Implement redo functionality
  }, []);

  const zoomIn = useCallback(() => {
    if (!stage) return;
    const scale = stage.scaleX();
    stage.scale({ x: scale * 1.2, y: scale * 1.2 });
    stage.draw();
  }, [stage]);

  const zoomOut = useCallback(() => {
    if (!stage) return;
    const scale = stage.scaleX();
    stage.scale({ x: scale / 1.2, y: scale / 1.2 });
    stage.draw();
  }, [stage]);

  const resetView = useCallback(() => {
    if (!stage) return;
    
    console.log('=== Resetting View ===');
    
    // Reset zoom and position
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.draw();
    
    console.log('View reset complete');
  }, [stage]);

  const cleanupCanvas = useCallback(() => {
    console.log('=== Cleanup Canvas Debug ===');
    console.log('Objects before cleanup:', canvasObjects.length);
    
    // Remove any objects that might be causing issues
    const validObjects = canvasObjects.filter(obj => {
      const isValid = obj.x !== undefined && obj.y !== undefined && 
                     !isNaN(obj.x) && !isNaN(obj.y);
      
      if (!isValid) {
        console.log('Removing invalid object:', obj);
      }
      
      return isValid;
    });
    
    setCanvasObjects(validObjects);
    console.log('Objects after cleanup:', validObjects.length);
  }, [canvasObjects]);

  const debugCanvas = useCallback(() => {
    console.log('=== Canvas Debug Info ===');
    console.log('Canvas dimensions:', getCanvasDimensions());
    console.log('Total objects:', canvasObjects.length);
    console.log('Draft objects:', draftObjects.length);
    
    // Log all objects with their properties
    canvasObjects.forEach((obj, index) => {
      console.log(`Object ${index}:`, {
        id: obj.id,
        name: obj.type,
        type: obj.type,
        x: obj.x,
        y: obj.y,
        width: obj.width,
        height: obj.height,
        draggable: obj.draggable,
        selectable: obj.selectable,
        data: obj.data
      });
    });
  }, [canvasObjects, draftObjects, getCanvasDimensions]);

  const clearClipping = useCallback(() => {
    console.log('=== Clearing Clipping ===');
    console.log('Konva.js doesn\'t use clipping in the same way as Fabric.js');
    console.log('No clipping to clear');
  }, []);

  // Handle object selection
  const handleObjectClick = useCallback((object) => {
    setSelectedObject(object);
    setIsPropertiesSidebarOpen(true);
    console.log('Object selected:', object);
  }, []);

  // Handle object drag start
  const handleObjectDragStart = useCallback((konvaObject) => {
    console.log('Object drag start:', konvaObject);
    
    // Get the object data from the Konva object
    const objectData = canvasObjects.find(obj => obj.id === konvaObject.id);
    if (!objectData) return;
    
    // Store the original object for potential restoration
    setDraggedObject(objectData);
    
    // Remove the object from canvas
    setCanvasObjects(prev => prev.filter(obj => obj.id !== objectData.id));
    
    // Simulate the same action as clicking the Add buttons
    if (objectData.type === 'window') {
      addWindow();
    } else if (objectData.type === 'door') {
      addDoor();
    } else if (objectData.type === 'product') {
      // For products, we need to pass the product data from the dragged object
      const productData = objectData.data?.productData || {
        id: 1,
        name: 'Default Product',
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        description: 'Default product description'
      };
      addProduct(productData);
    }
  }, [canvasObjects, addWindow, addDoor, addProduct]);

  // Handle object drag move - this will be handled by the existing mouse move logic for placement
  const handleObjectDragMove = useCallback((konvaObject) => {
    // The existing handleMouseMove will handle the placement preview and snap points
  }, []);

  // Handle object drag end - cancel and restore object
  const handleObjectDrag = useCallback((konvaObject) => {
    console.log('Object drag end - canceling and restoring');
    
  }, [draggedObject]);

  // Recalculate snap points when objects are updated
  const recalculateSnapPoints = useCallback(() => {
    setTimeout(() => calculateSnapPoints(), 100);
  }, [calculateSnapPoints]);

  // Handle mouse down for panning (disabled for now)
  const handleMouseDown = useCallback((e) => {
    // Canvas panning disabled for now
    // if (!stage) return;
    // 
    // // Only enable panning when not in placement mode and not clicking on objects
    // if (!isPlacingRoom && !isPlacingWindow && !isPlacingDoor && !isPlacingProduct && !draggedObject) {
    //   setIsPanning(true);
    //   const pointer = stage.getPointerPosition();
    //   setPanStart({ x: pointer.x, y: pointer.y });
    // }
  }, []);

  // Handle mouse move for room placement and panning
  const handleMouseMove = useCallback((e) => {
    if (!stage) return;

    // Handle panning (disabled for now)
    // if (isPanning) {
    //   const pointer = stage.getPointerPosition();
    //   const dx = pointer.x - panStart.x;
    //   const dy = pointer.y - panStart.y;
    //   
    //   // Update stage position
    //   const newX = stage.x() + dx;
    //   const newY = stage.y() + dy;
    //   stage.position({ x: newX, y: newY });
    //   stage.draw();
    //   
    //   // Update pan start position
    //   setPanStart({ x: pointer.x, y: pointer.y });
    //   return;
    // }

    // Handle placement modes (including when dragging an object)
    if (!isPlacingRoom && !isPlacingWindow && !isPlacingDoor && !isPlacingProduct) return;

    const pointer = stage.getPointerPosition();
    

    
    if (isPlacingRoom) {
      const nearestSnap = findNearestSnapPoint(pointer.x, pointer.y);
      
      if (nearestSnap) {
        // Calculate room position based on snap point
        let roomX, roomY;
        
        switch (nearestSnap.type) {
          case 'top':
            roomX = nearestSnap.x - roomDimensions.width / 2;
            roomY = nearestSnap.y - roomDimensions.height;
            break;
          case 'bottom':
            roomX = nearestSnap.x - roomDimensions.width / 2;
            roomY = nearestSnap.y;
            break;
          case 'left':
            roomX = nearestSnap.x - roomDimensions.width;
            roomY = nearestSnap.y - roomDimensions.height / 2;
            break;
          case 'right':
            roomX = nearestSnap.x;
            roomY = nearestSnap.y - roomDimensions.height / 2;
            break;
          default:
            roomX = nearestSnap.x - roomDimensions.width / 2;
            roomY = nearestSnap.y - roomDimensions.height / 2;
        }
        
        // Validate placement
        const isValid = validateRoomPlacement(roomX, roomY, roomDimensions.width, roomDimensions.height);
        
        setPlacementPreview({
          x: roomX,
          y: roomY,
          width: roomDimensions.width,
          height: roomDimensions.height,
          isValid
        });
      } else {
        setPlacementPreview(null);
      }
    } else if (isPlacingWindow || isPlacingDoor) {
      // Find nearest window/door snap point
      let nearestPoint = null;
      let minDistance = Infinity;
      
      windowDoorSnapPoints.forEach(point => {
        const distance = Math.sqrt(Math.pow(pointer.x - point.x, 2) + Math.pow(pointer.y - point.y, 2));
        if (distance < minDistance && distance < 30) { // 30px snap distance for windows/doors
          minDistance = distance;
          nearestPoint = point;
        }
      });
      
      if (nearestPoint) {
        const isWindow = isPlacingWindow;
        const length = isWindow ? windowLength : doorLength;
        
        // Calculate position based on wall type
        let x, y, width, height;
        if (nearestPoint.wallType === 'horizontal') {
          // For horizontal walls, window/door should be horizontal (parallel to wall)
          x = nearestPoint.x - length / 2; // Center the length
          y = nearestPoint.y - 4; // Center the 8px width
          width = length;
          height = 8;
        } else {
          // For vertical walls, window/door should be vertical (parallel to wall)
          x = nearestPoint.x - 4; // Center the 8px width
          y = nearestPoint.y - length / 2; // Center the length
          width = 8;
          height = length;
        }
        
        setPlacementPreview({
          x,
          y,
          width,
          height,
          isValid: true,
          snapPoint: nearestPoint,
          isWindow
        });
      } else {
        setPlacementPreview(null);
      }
    } else if (isPlacingProduct) {
      // First try to find wall snap points (for products 1, 2, and 3)
      const productId = currentProduct?.id;
      
      if (productId === 1 || productId === 2 || productId === 3) {
        // For rectangular products, use wall snap points (like windows)
        let nearestPoint = null;
        let minDistance = Infinity;
        
        windowDoorSnapPoints.forEach(point => {
          const distance = Math.sqrt(Math.pow(pointer.x - point.x, 2) + Math.pow(pointer.y - point.y, 2));
          if (distance < minDistance && distance < 30) { // 30px snap distance for wall products
            minDistance = distance;
            nearestPoint = point;
          }
        });
        
        if (nearestPoint) {
          // Calculate preview dimensions based on product type and wall orientation
          let previewWidth, previewHeight;
          const length = productId === 3 ? windowLength * 2 : windowLength; // Product 3 is double length
          
          if (nearestPoint.wallType === 'horizontal') {
            previewWidth = length;
            previewHeight = 8;
          } else {
            previewWidth = 8;
            previewHeight = length;
          }
          
          setPlacementPreview({
            x: nearestPoint.x - previewWidth / 2,
            y: nearestPoint.y - previewHeight / 2,
            width: previewWidth,
            height: previewHeight,
            isValid: true,
            snapPoint: nearestPoint,
            isProduct: true,
            wallType: nearestPoint.wallType
          });
        } else {
          setPlacementPreview(null);
        }
      } else {
        // For circular products, use grid snap points
        let nearestPoint = null;
        let minDistance = Infinity;
        
        productGridSnapPoints.forEach(point => {
          const distance = Math.sqrt(Math.pow(pointer.x - point.x, 2) + Math.pow(pointer.y - point.y, 2));
          if (distance < minDistance && distance < 25) { // 25px snap distance for products
            minDistance = distance;
            nearestPoint = point;
          }
        });
        
        if (nearestPoint) {
          // Default: Circle (represented as square in preview)
          const previewWidth = 20; // Circle diameter
          const previewHeight = 20;
          
          setPlacementPreview({
            x: nearestPoint.x - previewWidth / 2, // Center the preview
            y: nearestPoint.y - previewHeight / 2, // Center the preview
            width: previewWidth,
            height: previewHeight,
            isValid: true,
            snapPoint: nearestPoint,
            isProduct: true
          });
        } else {
          setPlacementPreview(null);
        }
      }
    }
  }, [isPlacingRoom, isPlacingWindow, isPlacingDoor, isPlacingProduct, stage, findNearestSnapPoint, roomDimensions, validateRoomPlacement, windowDoorSnapPoints, windowLength, doorLength, productGridSnapPoints, currentProduct]);

  // Handle mouse click for room placement
  const handleMouseClick = useCallback((e) => {
    if (!placementPreview) return;

    if (isPlacingRoom && placementPreview.isValid) {
      addRoomAtPosition(
        placementPreview.x + placementPreview.width / 2,
        placementPreview.y + placementPreview.height / 2
      );
      
      // Exit placement mode
      setIsPlacingRoom(false);
      setPlacementPreview(null);
    } else if (isPlacingWindow && placementPreview.isValid) {
      addWindowAtPosition(
        placementPreview.snapPoint.x,
        placementPreview.snapPoint.y,
        placementPreview.snapPoint.wallType
      );
      
      // Exit placement mode
      setIsPlacingWindow(false);
      setPlacementPreview(null);
    } else if (isPlacingDoor && placementPreview.isValid) {
      // Add anchor point for door placement
      const newAnchorPoint = {
        x: placementPreview.snapPoint.x,
        y: placementPreview.snapPoint.y,
        wallType: placementPreview.snapPoint.wallType
      };
      
      setDoorAnchorPoints(prev => [...prev, newAnchorPoint]);
      
      // If we have 2 anchor points, create the door
      if (doorAnchorPoints.length === 1) {
        const firstPoint = doorAnchorPoints[0];
        const secondPoint = newAnchorPoint;
        
        // Calculate door length based on distance between anchor points
        const distance = Math.sqrt(
          Math.pow(secondPoint.x - firstPoint.x, 2) + 
          Math.pow(secondPoint.y - firstPoint.y, 2)
        );
        
        // Use the first point as the door position and the calculated length
        addDoorAtPosition(
          firstPoint.x,
          firstPoint.y,
          firstPoint.wallType,
          distance
        );
        
        // Exit placement mode
        setIsPlacingDoor(false);
        setPlacementPreview(null);
        setDoorAnchorPoints([]);
      }
    } else if (isPlacingProduct && placementPreview.isValid) {
      // Pass wall type for rectangular products (1, 2, 3) or default for circular products
      const wallType = placementPreview.wallType || 'horizontal';
      
      addProductAtPosition(
        placementPreview.snapPoint.x,
        placementPreview.snapPoint.y,
        currentProduct,
        wallType
      );
      
      // Exit placement mode
      setIsPlacingProduct(false);
      setPlacementPreview(null);
      setCurrentProduct(null);
    }
  }, [isPlacingRoom, isPlacingWindow, isPlacingDoor, isPlacingProduct, placementPreview, doorAnchorPoints, addRoomAtPosition, addWindowAtPosition, addDoorAtPosition, addProductAtPosition, currentProduct]);

  // Handle mouse up to stop panning (disabled for now)
  const handleMouseUp = useCallback(() => {
    // setIsPanning(false);
  }, []);

  // Handle escape key to cancel room placement
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && (isPlacingRoom || isPlacingWindow || isPlacingDoor || isPlacingProduct)) {
        setIsPlacingRoom(false);
        setIsPlacingWindow(false);
        setIsPlacingDoor(false);
        setIsPlacingProduct(false);
        setPlacementPreview(null);
        setCurrentProduct(null);
        setDoorAnchorPoints([]); // Clear door anchor points
        
        // Restore dragged object if we were dragging
        if (draggedObject) {
          setCanvasObjects(prev => [...prev, draggedObject]);
          setDraggedObject(null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPlacingRoom, isPlacingWindow, isPlacingDoor, isPlacingProduct, draggedObject]);

  // Recalculate snap points when canvas objects change
  useEffect(() => {
    if (canvasObjects.length > 0) {
      calculateSnapPoints();
      calculateWindowDoorSnapPoints();
      calculateProductGridSnapPoints(); // Recalculate product grid snap points
    }
  }, [canvasObjects, calculateSnapPoints, calculateWindowDoorSnapPoints, calculateProductGridSnapPoints]);

  // Handle object property updates
  const handleObjectUpdate = useCallback((updatedObject) => {
    console.log('Updating object:', updatedObject);
    
    // Update the object in the appropriate array
    if (canvasObjects.some(obj => obj.id === updatedObject.id)) {
      setCanvasObjects(prev => 
        prev.map(obj => obj.id === updatedObject.id ? updatedObject : obj)
      );
    } else if (draftObjects.some(obj => obj.id === updatedObject.id)) {
      setDraftObjects(prev => 
        prev.map(obj => obj.id === updatedObject.id ? updatedObject : obj)
      );
    }
    
    // Update the selected object
    setSelectedObject(updatedObject);
    recalculateSnapPoints(); // Recalculate snap points after any object is updated
  }, [canvasObjects, draftObjects, recalculateSnapPoints]);

  // Handle object deletion
  const handleDeleteObject = useCallback((objectToDelete) => {
    console.log('Deleting object:', objectToDelete);
    
    if (objectToDelete.type === 'room') {
      // For rooms, delete the room and all objects inside it
      const roomBounds = {
        x: objectToDelete.x,
        y: objectToDelete.y,
        width: objectToDelete.width,
        height: objectToDelete.height
      };
      
      // Find all objects that are inside this room
      const objectsToDelete = canvasObjects.filter(obj => {
        if (obj.id === objectToDelete.id) return true; // The room itself
        
        // Check if object is inside the room bounds
        const objCenterX = obj.x + (obj.width || 0) / 2;
        const objCenterY = obj.y + (obj.height || 0) / 2;
        
        return objCenterX >= roomBounds.x && 
               objCenterX <= roomBounds.x + roomBounds.width &&
               objCenterY >= roomBounds.y && 
               objCenterY <= roomBounds.y + roomBounds.height;
      });
      
      console.log('Deleting room and objects inside:', objectsToDelete.length);
      
      // Remove all objects that are inside the room
      setCanvasObjects(prev => prev.filter(obj => 
        !objectsToDelete.some(toDelete => toDelete.id === obj.id)
      ));
      
      // Also remove from draft objects
      setDraftObjects(prev => prev.filter(obj => 
        !objectsToDelete.some(toDelete => toDelete.id === obj.id)
      ));
    } else {
      // For other objects, just delete the selected object
      setCanvasObjects(prev => prev.filter(obj => obj.id !== objectToDelete.id));
      setDraftObjects(prev => prev.filter(obj => obj.id !== objectToDelete.id));
    }
    
    // Clear selection and close sidebar
    setSelectedObject(null);
    setIsPropertiesSidebarOpen(false);
    
    console.log('Object deletion complete');
  }, [canvasObjects]);

  // Close properties sidebar
  const handleClosePropertiesSidebar = useCallback(() => {
    setIsPropertiesSidebarOpen(false);
    setSelectedObject(null);
  }, []);

  // Add AI-generated objects to canvas
  const addAIGeneratedObjects = useCallback(async (aiObjects) => {
    console.log('Adding AI-generated objects to canvas:', aiObjects);
    
    if (!aiObjects || !Array.isArray(aiObjects)) {
      console.error('Invalid AI objects:', aiObjects);
      return;
    }

    try {
      const newObjects = [];
      let currentId = nextId;

      // Process each AI object and convert to canvas format
      aiObjects.forEach((aiObj) => {
        const canvasObject = {
          id: currentId++,
          type: aiObj.type,
          name: aiObj.name || `${aiObj.type} ${currentId}`,
          x: aiObj.x || 0,
          y: aiObj.y || 0,
          width: aiObj.width || 100,
          height: aiObj.height || 100,
          // Add any additional properties from AI
          ...(aiObj.data && { data: aiObj.data }),
          ...(aiObj.wallType && { wallType: aiObj.wallType })
        };

        // Validate object position and dimensions
        if (canvasObject.x < 0) canvasObject.x = 0;
        if (canvasObject.y < 0) canvasObject.y = 0;
        if (canvasObject.width < 10) canvasObject.width = 10;
        if (canvasObject.height < 10) canvasObject.height = 10;

        newObjects.push(canvasObject);
        console.log(`Processed AI object: ${canvasObject.type} at (${canvasObject.x}, ${canvasObject.y})`);
      });

      // Add objects to canvas
      setCanvasObjects(prev => [...prev, ...newObjects]);
      setNextId(currentId);

      // Recalculate snap points if rooms were added
      const hasRooms = newObjects.some(obj => obj.type === 'room');
      if (hasRooms) {
        setTimeout(() => {
          calculateSnapPoints();
          calculateWindowDoorSnapPoints();
          calculateProductGridSnapPoints();
        }, 100);
      }

      console.log(`Successfully added ${newObjects.length} AI-generated objects to canvas`);
      
      // Auto-save the canvas with new objects
      setTimeout(() => {
        saveCanvas();
      }, 200);

    } catch (error) {
      console.error('Error adding AI objects to canvas:', error);
      throw error;
    }
  }, [nextId, calculateSnapPoints, calculateWindowDoorSnapPoints, calculateProductGridSnapPoints, saveCanvas]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    addRoom,
    addWall,
    addWindow,
    addDoor,
    addProduct,
    addAIGeneratedObjects,
    deleteSelected,
    clearCanvas,
    saveCanvas,
    loadCanvas,
    undo,
    redo,
    zoomIn,
    zoomOut,
    resetView,
    cleanupCanvas,
    debugCanvas,
    clearClipping,
    setSelectedTool: (tool) => setSelectedTool(tool),
    setRoomDimensions: (dims) => setRoomDimensions(dims),
    setWallLength: (length) => setWallLength(length),
    setWindowLength: (length) => setWindowLength(length),
    setDoorLength: (length) => setDoorLength(length),
    selectedObject,
    selectedTool,
    roomDimensions,
    wallLength,
    windowLength,
    doorLength,
    draftObjectsCount: draftObjects.length,
    clearDrafts: () => setDraftObjects([])
  }), [
    addRoom, addWall, addWindow, addDoor, addProduct, deleteSelected, clearCanvas,
    saveCanvas, loadCanvas, undo, redo, zoomIn, zoomOut, resetView, cleanupCanvas,
    debugCanvas, clearClipping, selectedObject, selectedTool, roomDimensions, wallLength, windowLength, doorLength, draftObjects, recalculateSnapPoints
  ]);

  const dimensions = getCanvasDimensions();

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      <Stage
        ref={stageRef}
        width={dimensions.width}
        height={dimensions.height}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
                          cursor: isPlacingRoom ? 'crosshair' :
                        selectedTool === 'wall' ? 'crosshair' : 
                        selectedTool === 'room' ? 'pointer' :
                        selectedTool === 'window' ? 'pointer' :
                        selectedTool === 'door' ? 'pointer' : 
                        (!isPlacingRoom && !isPlacingWindow && !isPlacingDoor && !isPlacingProduct) ? 'default' : 'default'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onClick={handleMouseClick}
      >
        <Layer>
          {/* Render canvas objects */}
          {canvasObjects.map((obj) => {
            if (obj.type === 'room' || obj.type === 'window' || obj.type === 'door') {
              return (
                <Rect
                  key={obj.id}
                  x={obj.x}
                  y={obj.y}
                  width={obj.width}
                  height={obj.height}
                  fill={obj.fill}
                  stroke={obj.stroke}
                  strokeWidth={obj.strokeWidth}
                  draggable={obj.draggable}
                  onClick={() => handleObjectClick(obj)}
                  onDragStart={() => handleObjectDragStart(obj)}
                  onDragMove={() => handleObjectDragMove(obj)}
                  onDragEnd={() => handleObjectDrag(obj)}
                />
              );
            } else if (obj.type === 'product') {
              // Render based on product shape
              if (obj.shape === 'rectangle') {
                return (
                  <Rect
                    key={obj.id}
                    x={obj.x}
                    y={obj.y}
                    width={obj.width}
                    height={obj.height}
                    fill={obj.fill}
                    stroke={obj.stroke}
                    strokeWidth={obj.strokeWidth}
                    draggable={obj.draggable}
                    onClick={() => handleObjectClick(obj)}
                    onDragStart={() => handleObjectDragStart(obj)}
                    onDragMove={() => handleObjectDragMove(obj)}
                    onDragEnd={() => handleObjectDrag(obj)}
                  />
                );
              } else {
                // Default to circle shape
                return (
                  <Circle
                    key={obj.id}
                    x={obj.x + obj.radius}
                    y={obj.y + obj.radius}
                    radius={obj.radius}
                    fill={obj.fill}
                    stroke={obj.stroke}
                    strokeWidth={obj.strokeWidth}
                    draggable={obj.draggable}
                    onClick={() => handleObjectClick(obj)}
                    onDragStart={() => handleObjectDragStart(obj)}
                    onDragMove={() => handleObjectDragMove(obj)}
                    onDragEnd={() => handleObjectDrag(obj)}
                  />
                );
              }
            } else if (obj.type === 'wall') {
              return (
                <Line
                  key={obj.id}
                  points={obj.points}
                  stroke={obj.stroke}
                  strokeWidth={obj.strokeWidth}
                  draggable={obj.draggable}
                  onClick={() => handleObjectClick(obj)}
                  onDragEnd={() => handleObjectDrag(obj)}
                />
              );
            }
            return null;
          })}
          
          {/* Render draft objects */}
          {draftObjects.map((obj) => {
            if (obj.type === 'room' || obj.type === 'window' || obj.type === 'door') {
              return (
                <Rect
                  key={obj.id}
                  x={obj.x}
                  y={obj.y}
                  width={obj.width}
                  height={obj.height}
                  fill={obj.fill}
                  stroke={obj.stroke}
                  strokeWidth={obj.strokeWidth}
                  draggable={obj.draggable}
                  opacity={0.7} // Make draft objects slightly transparent
                  onClick={() => handleObjectClick(obj)}
                  onDragStart={() => handleObjectDragStart(obj)}
                  onDragMove={() => handleObjectDragMove(obj)}
                  onDragEnd={() => handleObjectDrag(obj)}
                />
              );
            } else if (obj.type === 'product') {
              // Render based on product shape
              if (obj.shape === 'rectangle') {
                return (
                  <Rect
                    key={obj.id}
                    x={obj.x}
                    y={obj.y}
                    width={obj.width}
                    height={obj.height}
                    fill={obj.fill}
                    stroke={obj.stroke}
                    strokeWidth={obj.strokeWidth}
                    draggable={obj.draggable}
                    opacity={0.7} // Make draft objects slightly transparent
                    onClick={() => handleObjectClick(obj)}
                    onDragStart={() => handleObjectDragStart(obj)}
                    onDragMove={() => handleObjectDragMove(obj)}
                    onDragEnd={() => handleObjectDrag(obj)}
                  />
                );
              } else {
                // Default to circle shape
                return (
                  <Circle
                    key={obj.id}
                    x={obj.x + obj.radius}
                    y={obj.y + obj.radius}
                    radius={obj.radius}
                    fill={obj.fill}
                    stroke={obj.stroke}
                    strokeWidth={obj.strokeWidth}
                    draggable={obj.draggable}
                    opacity={0.7} // Make draft objects slightly transparent
                    onClick={() => handleObjectClick(obj)}
                    onDragStart={() => handleObjectDragStart(obj)}
                    onDragMove={() => handleObjectDragMove(obj)}
                    onDragEnd={() => handleObjectDrag(obj)}
                  />
                );
              }
            } else if (obj.type === 'wall') {
              return (
                <Line
                  key={obj.id}
                  points={obj.points}
                  stroke={obj.stroke}
                  strokeWidth={obj.strokeWidth}
                  draggable={obj.draggable}
                  opacity={0.7} // Make draft objects slightly transparent
                  onClick={() => handleObjectClick(obj)}
                  onDragEnd={() => handleObjectDrag(obj)}
                />
              );
            }
            return null;
          })}

          {/* Render snap points when placing room */}
          {isPlacingRoom && snapPoints.map((point, index) => (
            <Rect
              key={`snap_${index}`}
              x={point.x - 3}
              y={point.y - 3}
              width={6}
              height={6}
              fill="#00ff00"
              stroke="#333"
              strokeWidth={1}
            />
          ))}

          {/* Render window/door snap points */}
          {(isPlacingWindow || isPlacingDoor || (isPlacingProduct && currentProduct && (currentProduct.id === 1 || currentProduct.id === 2 || currentProduct.id === 3))) && windowDoorSnapPoints.map((point, index) => (
            <Rect
              key={`windowDoorSnap_${index}`}
              x={point.x - 2}
              y={point.y - 2}
              width={4}
              height={4}
              fill={isPlacingWindow ? "#0066ff" : isPlacingDoor ? "#ff6600" : "#9933ff"}
              stroke={isPlacingWindow ? "#0044cc" : isPlacingDoor ? "#cc4400" : "#6600cc"}
              strokeWidth={1}
            />
          ))}

          {/* Render product grid snap points */}
          {(isPlacingRoom || (isPlacingProduct && currentProduct && !(currentProduct.id === 1 || currentProduct.id === 2 || currentProduct.id === 3))) && productGridSnapPoints.map((point, index) => (
            <Rect
              key={`productGridSnap_${index}`}
              x={point.x - 2}
              y={point.y - 2}
              width={4}
              height={4}
              fill="#00ff00"
              stroke="#00aa00"
              strokeWidth={1}
            />
          ))}

          {/* Render placement preview */}
          {placementPreview && (
            <Rect
              x={placementPreview.x}
              y={placementPreview.y}
              width={placementPreview.width}
              height={placementPreview.height}
              fill={placementPreview.isValid ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)'}
              stroke={placementPreview.isValid ? '#00ff00' : '#ff0000'}
              strokeWidth={2}
              dash={[5, 5]}
            />
          )}
        </Layer>
      </Stage>

      {/* Properties Sidebar */}
      <ObjectPropertiesSidebar
        selectedObject={selectedObject}
        onUpdateObject={handleObjectUpdate}
        onDeleteObject={handleDeleteObject}
        isOpen={isPropertiesSidebarOpen}
        onClose={handleClosePropertiesSidebar}
      />

      {/* Room Placement Status */}
      {isPlacingRoom && (
        <Box
          position="absolute"
          top={4}
          left={4}
          bg="blue.500"
          color="white"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          zIndex={1001}
        >
          🏠 Room Placement Mode - Click on green snap points to place room
          <Text fontSize="xs" mt={1} opacity={0.8}>
            Press ESC to cancel
          </Text>
        </Box>
      )}

      {/* Window Placement Status */}
      {isPlacingWindow && (
        <Box
          position="absolute"
          top={4}
          left={4}
          bg="blue.500"
          color="white"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          zIndex={1001}
        >
          🪟 Window Placement Mode - Click on blue snap points to place window
          <Text fontSize="xs" mt={1} opacity={0.8}>
            Press ESC to cancel
          </Text>
        </Box>
      )}

      {/* Door Placement Status */}
      {isPlacingDoor && (
        <Box
          position="absolute"
          top={4}
          left={4}
          bg="orange.500"
          color="white"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          zIndex={1001}
        >
          🚪 Door Placement Mode - Click on orange snap points to place door
          <Text fontSize="xs" mt={1} opacity={0.8}>
            Press ESC to cancel
          </Text>
        </Box>
      )}

      {/* Product Placement Status */}
      {isPlacingProduct && (
        <Box
          position="absolute"
          top={4}
          left={4}
          bg="purple.500"
          color="white"
          px={4}
          py={2}
          borderRadius="md"
          fontSize="sm"
          fontWeight="medium"
          zIndex={1001}
        >
          📦 Product Placement Mode - Click on {
            currentProduct && (currentProduct.id === 1 || currentProduct.id === 2 || currentProduct.id === 3) 
              ? 'purple wall snap points' 
              : 'green grid points'
          } to place product
          <Text fontSize="xs" mt={1} opacity={0.8}>
            Press ESC to cancel
          </Text>
        </Box>
      )}
    </Box>
  );
});

FloorPlanCanvas.displayName = 'FloorPlanCanvas';

export default FloorPlanCanvas; 